import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { useFacilityStore } from "../../../../app/z-store/store";
import { getSupplierUrl } from "../../../../utils/urls";

const ViewSupplier = () => {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedFacilityId && supplierId) {
      fetchSupplierDetails();
    }
  }, [selectedFacilityId, supplierId]);

  const fetchSupplierDetails = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getSupplierUrl}/${selectedFacilityId}/${supplierId}`,
        'GET'
      );

      if (response.success && response.data) {
        setSupplier(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch supplier details', 'error');
        console.error('Error fetching supplier details:', response);
      }
    } catch (error) {
      toastify('Error fetching supplier details', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // If no facility is selected, show a prompt
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            Please select a facility to view supplier details.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/supplier_management">
                    Suppliers
                  </Link>
                </li>
                <li className="breadcrumb-item">View Supplier</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main content card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <Link to="/facility/procurement_management/supplier_management">
                  <i className="ti ti-arrow-narrow-left"></i> Back
                </Link>
              </div>
              <div>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/facility/procurement_management/supplier/view/${supplierId}`)}
                >
                  Edit Supplier
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          ) : supplier ? (
            <div className="card">
              <div className="card-header">
                <h5>Supplier Details</h5>
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Basic Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Supplier Name:</td>
                              <td>{supplier.name || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Email Address:</td>
                              <td>{supplier.email || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Phone Number:</td>
                              <td>{supplier.phone || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Tax ID Number:</td>
                              <td>{supplier.taxIdentificationNumber || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Status:</td>
                              <td>
                                <span className={`badge ${supplier.status === 'active' ? 'bg-success' : supplier.status === 'pending' ? 'bg-warning' : 'bg-secondary'}`}>
                                  {supplier.status ? supplier.status.charAt(0).toUpperCase() + supplier.status.slice(1) : 'N/A'}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Contact Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Contact Person:</td>
                              <td>{supplier.contactPerson?.name || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Department:</td>
                              <td>{supplier.department?.name || 'N/A'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Documents Section */}
                {supplier.documents && supplier.documents.length > 0 && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="card">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Documents</h6>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>Document Name</th>
                                  <th>Document Type</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {supplier.documents.map((doc, index) => (
                                  <tr key={index}>
                                    <td>{doc.documentName || 'N/A'}</td>
                                    <td>
                                      {doc.documentType ?
                                        doc.documentType.charAt(0).toUpperCase() + doc.documentType.slice(1)
                                        : 'N/A'}
                                    </td>
                                    <td>
                                      {doc.document && (
                                        <a
                                          href={doc.document}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="btn btn-sm btn-outline-primary"
                                        >
                                          <i className="ti ti-eye me-1"></i> View
                                        </a>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center">
                <p>Supplier not found or unable to fetch details.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ViewSupplier;